import { rpc, TeamRpcError } from "@/lib/team/api";
import type { GroupKind } from "./groups";
import type { ItemProfessionalRow } from "./item-professionals";
import type {
  CatalogGroup, CatalogItem, CommissionModel, CommissionReportRow, MemberCommissionSetup, MemberModel, Professional,
} from "./types";

const CODES = new Set([
  "commission_invalid_group", "commission_invalid_item", "commission_invalid_percent", "commission_invalid_model",
  "team_forbidden", "team_not_member", "team_self_locked", "team_owner_locked",
]);

export function commissionErrorKey(error: unknown): string {
  const code = error instanceof TeamRpcError ? error.code : "";
  return CODES.has(code) ? `errors.${code}` : "errors.generic";
}

export type ReportFilters = { from: string; to: string; professionalId: string | null; kind: GroupKind | null; groupId: string | null };

export const commissionsApi = {
  catalogGroups: () => rpc<CatalogGroup[]>("commission_catalog_groups"),
  catalogItems: () => rpc<CatalogItem[]>("commission_catalog_items"),
  listModels: () => rpc<CommissionModel[]>("commission_list_models"),
  saveModel: (m: { id: string | null; name: string; basis: "gross" | "after_discount"; isActive: boolean }) =>
    rpc<string>("commission_save_model", { p_id: m.id, p_name: m.name, p_basis: m.basis, p_is_active: m.isActive }),
  setRate: (modelId: string, kind: GroupKind, groupId: string, percent: number | null) =>
    rpc<void>("commission_set_rate", { p_model_id: modelId, p_kind: kind, p_group_id: groupId, p_percent: percent }),
  setItemRate: (modelId: string, itemId: string, percent: number | null) =>
    rpc<void>("commission_set_item_rate", { p_model_id: modelId, p_item_id: itemId, p_percent: percent }),
  clearGroupItemRates: (modelId: string, kind: GroupKind, groupId: string) =>
    rpc<void>("commission_clear_group_item_rates", { p_model_id: modelId, p_kind: kind, p_group_id: groupId }),
  memberModels: () => rpc<MemberModel[]>("commission_member_models"),
  setModelMembers: (modelId: string, userIds: string[]) =>
    rpc<void>("commission_set_model_members", { p_model_id: modelId, p_user_ids: userIds }),
  memberSetup: (userId: string) => rpc<MemberCommissionSetup>("commission_member_setup", { p_user_id: userId }),
  setMemberModel: (userId: string, modelId: string | null) =>
    rpc<void>("commission_set_member_model", { p_user_id: userId, p_model_id: modelId }),
  setOverride: (userId: string, kind: GroupKind, groupId: string, percent: number | null) =>
    rpc<void>("commission_set_override", { p_user_id: userId, p_kind: kind, p_group_id: groupId, p_percent: percent }),
  setItemOverride: (userId: string, itemId: string, percent: number | null) =>
    rpc<void>("commission_set_item_override", { p_user_id: userId, p_item_id: itemId, p_percent: percent }),
  professionals: () => rpc<Professional[]>("commission_professionals"),
  itemProfessionals: () => rpc<ItemProfessionalRow[]>("commission_item_professionals"),
  report: (f: ReportFilters) =>
    rpc<CommissionReportRow[]>("commission_report", {
      p_from: f.from, p_to: f.to, p_professional_id: f.professionalId, p_kind: f.kind, p_group_id: f.groupId,
    }),
};
